import type { FC, ReactNode } from "react";
import { useMemo } from "react";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import useHousehold from "src/contexts/household-context";
import { useProgress } from "src/contexts/progress-context";

import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import MedicationOutlined from "@mui/icons-material/MedicationOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import { paths } from "src/paths";

export interface Item {
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  items?: Item[];
  label?: ReactNode;
  path?: string;
  title: string;
}

export interface Section {
  items: Item[];
  subheader?: string;
}

/** Small, subtle progress count rendered to the right of a nav row. */
const NavCount: FC<{ children: ReactNode; highlight?: boolean }> = ({
  children,
  highlight,
}) => (
  <Box
    component="span"
    sx={{
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1,
      px: 0.75,
      py: "3px",
      borderRadius: 100,
      minWidth: 16,
      textAlign: "center",
      color: highlight ? "primary.main" : "#8a8a8a",
      backgroundColor: highlight
        ? (theme) => alpha(theme.palette.primary.main, 0.12)
        : "#ededed",
    }}
  >
    {children}
  </Box>
);

const countLabel = (n: number): ReactNode =>
  n > 0 ? <NavCount>{n}</NavCount> : undefined;

export const useSections = () => {
  const { selectedHousehold, selectedUdId } = useHousehold();
  const progress = useProgress();
  const { documents, assets, modules } = progress;

  const head = selectedHousehold?.head_of_household;

  // The Life Details links point at whichever member is currently in context
  // (defaulting to the head of household). The per-member switch now lives on
  // the category page itself rather than as a nav accordion.
  const memberUdId = selectedUdId || head?.ud_id;

  return useMemo(
    () => {
      return [
        {
          items: [
            {
              title: "Home",
              path: paths.dashboard.index,
              icon: <HomeOutlinedIcon sx={{ fontSize: 18 }} />,
            },
            {
              title: "Sharing",
              path: paths.dashboard.accountSharing("sharing"),
              icon: <PersonAddOutlinedIcon sx={{ fontSize: 18 }} />,
            },
          ],
        },
        {
          subheader: "Planning",
          items: [
            {
              title: "Documents",
              path: paths.dashboard.planning.documents,
              icon: <FolderOutlinedIcon sx={{ fontSize: 18 }} />,
              label:
                documents.total > 0 ? (
                  <NavCount highlight={documents.status === "complete"}>
                    {documents.complete}
                  </NavCount>
                ) : undefined,
            },
            {
              title: "Assets",
              path: paths.dashboard.planning.assets,
              icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />,
              label: countLabel(assets.count),
            },
            {
              title: "Letter",
              path: paths.dashboard.planning.letterToFamily,
              icon: <EmailOutlinedIcon sx={{ fontSize: 18 }} />,
            },
          ],
        },
        {
          subheader: "Life Details",
          items: [
            {
              title: "Personal",
              icon: <PeopleOutlineIcon sx={{ fontSize: 18 }} />,
              path: `/${memberUdId}/personal`,
              label: countLabel(modules.personal),
            },
            {
              title: "Legal",
              icon: <FolderOutlinedIcon sx={{ fontSize: 18 }} />,
              path: `/${memberUdId}/legal`,
              label: countLabel(modules.legal),
            },
            {
              title: "Medical",
              icon: <MedicationOutlined sx={{ fontSize: 18 }} />,
              path: `/${memberUdId}/medical`,
              label: countLabel(modules.medical),
            },
            {
              title: "Financial",
              icon: <SavingsOutlinedIcon sx={{ fontSize: 18 }} />,
              path: `/${memberUdId}/financial`,
              label: countLabel(modules.financial),
            },
            {
              title: "Property",
              icon: <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />,
              path: `/${memberUdId}/property`,
              label: countLabel(modules.property),
            },
            {
              title: "Digital",
              icon: <PhoneAndroidOutlinedIcon sx={{ fontSize: 18 }} />,
              path: `/${memberUdId}/digital`,
              label: countLabel(modules.digital),
            },
            {
              title: "Legacy",
              icon: <AutoStoriesOutlinedIcon sx={{ fontSize: 18 }} />,
              path: `/${memberUdId}/legacy`,
              label: countLabel(modules.legacy),
            },
          ],
        },
      ];
    },
    [
      memberUdId,
      documents.complete,
      documents.total,
      documents.status,
      assets.count,
      modules.personal,
      modules.legal,
      modules.medical,
      modules.financial,
      modules.property,
      modules.digital,
      modules.legacy,
    ]
  );
};